import { z } from "zod";

// Pagination

function createPaginationSchema<NodeType extends z.ZodTypeAny>(nodeSchema: NodeType) {
  return z.object({
    edges: z.array(z.object({
      cursor: z.string(),
      node: nodeSchema
    })),
    pageInfo: z.object({
      hasNextPage: z.boolean(),
      hasPreviousPage: z.boolean(),
      startCursor: z.string().nullable(),
      endCursor: z.string().nullable()
    }).optional(),
    totalCount: z.number().optional()
  });
}

const zodWhereOperatorSchema = z.object({
  eq: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
  ne: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
  in: z.union([z.array(z.string()), z.array(z.number()), z.array(z.boolean()), z.null()]).optional(),
  like: z.union([z.string(), z.null()]).optional(),
  gt: z.union([z.string(), z.number(), z.null()]).optional(),
  gte: z.union([z.string(), z.number(), z.null()]).optional(),
  lt: z.union([z.string(), z.number(), z.null()]).optional(),
  lte: z.union([z.string(), z.number(), z.null()]).optional(),
});

export const zodWhereArgument = z.object({
  where: z.record(z.union([zodWhereOperatorSchema, z.array(z.record(z.string(), zodWhereOperatorSchema))])).nullish(),
});

// User

export const zodUserNode = z.object({
  email: z.string(),
  id: z.string(),
  name: z.string(),
  nickname: z.string(),
  roles: z.array(z.string()),
  salesforce_roles: z.string().nullable()
});

export const zodUserConnection = z.object({
  users: createPaginationSchema(zodUserNode)
});

// Chargers and Charger Monitors

const zodOcppStatus = z.enum(["Available", "Charging", "Faulted", "Finishing", "Preparing", "SuspendedEV", "SuspendedEVSE", "Unavailable", "Unknown"]);
const zodEvosCustomStatus = z.enum(["Offline", "RebootAfterReset"]);
const zodOcppFirmwareStatus = z.enum(["DownloadFailed", "Downloaded", "Downloading", "Idle", "InstallationFailed", "Installed", "Installing"]);

export const zodDashboardOcppStatusFrequencies = z.object({
  Available: z.number(),
  Charging: z.number(),
  Faulted: z.number(),
  Finishing: z.number(),
  Offline: z.number(),
  Preparing: z.number(),
  SuspendedEV: z.number(),
  SuspendedEVSE: z.number(),
  Unavailable: z.number()
});

export const zodChargerStatuses = z.object({
  dashboard_ocpp_status_frequencies: zodDashboardOcppStatusFrequencies.optional(),
}).passthrough();

export const zodChargerStatusMessageNode = z.object({
  errorCode: z.string(),
  receiveTimestamp: z.string(),
  serial: z.string(),
  status: zodOcppStatus,
  vendorErrorCode: z.string().nullable(),
  info: z.string().nullable()
});

export const zodzodChargerStatusMessageConnection = createPaginationSchema(zodChargerStatusMessageNode);

export const zodChargerMonitors = z.object({
  charger_monitors: z.object({
    charger_status_messages: zodzodChargerStatusMessageConnection,
    status: zodChargerStatuses.optional(),
    total_energy: z.array(z.object({
      from: z.string(),
      to: z.string(),
      total: z.number()
    }))
  })
});

export const zodSalesforceDetails = z.object({
  ampol_installed_messaged: z.boolean().nullish(),
  ampol_installed_messaged_timestamp: z.string().nullish(), 
  account_id: z.string().nullish(),
  firmware_version: z.string().nullish(),
  id: z.string(),
  heartbeat_interval: z.number().nullish(),
  iccid: z.string().nullish(),
  imei: z.string().nullish(),
  imsi: z.string().nullish(),
  circuit_amp_limit: z.number().nullish(),
  circuit_phases: z.number().nullish(),
  installation_date: z.string().nullish(),
  installation_ref: z.string().nullish(),
  installation_customer_email: z.string().nullish(),
  city: z.string().nullish(),
  country: z.string().refine((val) => val === "AU", {message: "Country must be AU"}).nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  last_comms: z.string().nullish(),
  last_heartbeat: z.string().nullish(),
  last_status_notification: z.string().nullish(),
  mode: z.string().nullish(),
  model: z.string().nullish(),
  name: z.string(),
  installer: z.string().nullish(),
  postcode: z.string().nullish(),
  province: z.string().nullish(),
  street_address: z.string().nullish(),
  suburb: z.string().nullish(),
  ocpp_id: z.string().nullish(),
  ocpp_server: z.string().nullish(),
  evos_custom_status: zodEvosCustomStatus.nullish(),
  ocpp_status: zodOcppStatus,
  ocpp_firmware_status: zodOcppFirmwareStatus.nullish(),
  owner_id: z.string().nullish(),
  serviced_by: z.string().nullish(),
  site_id: z.string().nullish(),
  status: z.string().nullish(),
  meterSerialNumber: z.string().nullish(),
  meterType: z.string().nullish(),
  vpn_ip: z.string().nullish(),
});

export const zodChargerNode = z.object({
  charger_status_messages: zodzodChargerStatusMessageConnection,
  serial: z.string(),
  total_energy: z.number(),
  charger_configuration: z.array(
    z.object({
      change_status: z.enum(["pending", "accepted"]),
      changed_timestamp: z.string().nullish(),
      charger_value: z.string().nullish(),
      config_key: z.string(),
      serial: z.string(),
      set_value: z.string().nullish(),
      readonly: z.boolean()
    })
  ),
  salesforce_details: zodSalesforceDetails,
  assetDetails: zodSalesforceDetails.optional(),
  details: zodSalesforceDetails,
  uptime: z.object({
    calculation_name: z.string(),
    calculation_type: z.enum(["string", "integer", "double"]),
    calculation_update_interval: z.number(),
    calculation_update_timestamp: z.string(),
    calculation_value: z.string()
  }),
  vpn_status: z.object({
    added_by: z.string().optional(),
    connections: z.array(z.object({
      allowed_ips: z.string().nullish(),
      endpoint: z.string().nullish(),
      latest_handshake: z.number().nullish(),
      peer_name: z.string().nullish(),
      receive_timestamp: z.string().nullish(),
      time_ago: z.string().nullish(),
      transfer_rx: z.string().nullish(),
      transfer_tx: z.string().nullish()
    })),
    created: z.string().nullish(),
    peer_desc: z.string().optional(),
    peer_name: z.string().optional(),
    peer_type: z.string().optional(),
    public_key: z.string().optional(),
    receive_timestamp: z.string().nullish(),
    updated: z.string().nullish()
  })
});

export const zodChargersConnection = z.object({
  chargers: createPaginationSchema(zodChargerNode)
});

// Service Logs and Monitors

export const zodServiceLogsNode = z.object({
  cluster_name: z.string(),
  code: z.enum([
    "CommunicationFailure",
    "MalformedMessage",
    "SalesforceQueryError",
    "UnauthorisedAccessAttempt",
    "DataflowError",
    "LogError",
    "WebsocketInfo",
    "WebsocketError",
    "AuthorisationInfo",
    "AuthorisationError",
    "NotFoundError",
    "Info",
    "ServiceFailure",
    "ConfigurationError",
    "VPNError",
    "InputError",
    "DatabaseError",
    "TestReportsError",
    "CometdError",
    "ChargerControlError",
    "BalancingError",
    "Warn",
    "ConnectorLockFailure",
    "EVCommunicationError",
    "GroundFailure",
    "HighTemperature",
    "InternalError",
    "LocalListConflict",
    "NoError",
    "OtherError",
    "OverCurrentFailure",
    "OverVoltage",
    "PowerMeterFailure",
    "PowerSwitchFailure",
    "ReaderFailure",
    "ResetFailure",
    "UnderVoltage",
    "WeakSignal",
    "NotImplemented",
    "NotSupported",
    "InternalError",
    "ProtocolError",
    "SecurityError",
    "FormationViolation",
    "PropertyConstraintViolation",
    "OccurenceConstraintViolation",
    "TypeConstraintViolation",
    "GenericError"
  ]),
  container_name: z.string(),
  context: z.string(),
  description: z.string(),
  env: z.string(),
  hostname: z.string(),
  id: z.number(),
  location: z.string(),
  log_filter_tag: z.string(),
  namespace: z.string(),
  pod_name: z.string(),
  receive_timestamp: z.string(),
  resource_type: z.string(),
  retries: z.number(),
  severity: z.string(),
  source: z.string(),
  stacktrace: z.string()
});

export const zodServiceLogsConnection = z.object({
  ServiceLogs: createPaginationSchema(zodServiceLogsNode)
});

export const zodServiceMonitors = z.object({
  service_error_counts: z.string(),
  service_status: z.string(),
  severities: z.object({
    error: z.number(),
    info: z.number(),
    warning: z.number()
  })
});

export const zodParsedServiceErrorCountsData = z.object({
  "vpn-graphql-service": z.optional(z.number()),
  "charger-control-service": z.optional(z.number()),
  "clj-occp-central-server": z.optional(z.number()),
  "charger-ssh-service": z.optional(z.number()),
  "charger-event-queuer": z.optional(z.number()),
  "iotdb-service": z.optional(z.number())
});

// Vehicles/VIDs

export const zodVidsCount = z.object({
  vehicles: z.object({
    totalCount: z.number()
  })
});

// Sites

export const zodSitesCount = z.object({
  sites: z.object({
    totalCount: z.number()
  })
});

// Charger Command Form Arguments

export const zodClearCacheFormArguments = z.object({
  serial: z.string()
});

export const zodGetConfigFormArguments = z.object({
  key: z.array(z.string())
});

const changeConfigValueSchema = z.preprocess(
  (val) => {
    if (typeof val === "number") {
      return val.toString();
    }
    if (typeof val === "string") {
      return val;
    }
    return "";
  },
  z.string()
    .trim()
    // Empty is allowed so a settable key the charger has not reported does not
    // block review of other edits. Submit still refuses to send a cleared value.
    .max(500, { message: "Value must be between 1 and 500 characters" })
);

export const zodChangeConfigFormArguments = z.record(z.string(), changeConfigValueSchema);

export const zodGetDiagnosticsFormArguments = z.object({
  location: z.string().min(1),
  retries: z.number().optional(),
  retryInterval: z.number().optional(),
  startTime: z.string().optional(),
  stopTime: z.string().optional()
});

export const zodResetFormArguments = z.object({
  type: z.enum(["Hard", "Soft"])
});

export const zodUpdateFirmwareFormArguments = z.object({
  location: z.string().min(1),
  retries: z.number().optional(),
  retrieveDate: z.string().optional(),
  retryInterval: z.number().optional(),
});

export const zodConfigBuilderFormArguments = z.object({
  configItems: z.array(z.object({
    name: z.string(),
    set_value: z.string()
      .trim()
      .min(1, { message: "Value is required" })
      .max(500, { message: "Value must be between 1 and 500 characters" })
  })),
  serialSelectionType: z.enum(["multiSelect", "range"]),
  serials: z.array(z.string()).refine((val) => val.length > 0, {message: "Serials must not be empty"})
}).refine((val) => val.serialSelectionType === "multiSelect" || (val.serialSelectionType === "range" && val.serials[0] < val.serials[1]), {message: "Serials must be in ascending order when using range selection"});

// Charger Command Result Data 

export const zodChangeConfigData = z.object({
  charger_ocpp_mutation: z.object({
    change_configuration: z.object({
      payload: z.object({
        status: z.string()
      })
    })
  })
});

export const zodResetChargerData = z.object({
  charger_ocpp_mutation: z.object({
    reset: z.object({
      payload: z.object({
        status: z.string()
      })
    })
  })
});

export type WhereArgument = z.infer<typeof zodWhereArgument>;

export type UserNode = z.infer<typeof zodUserNode>;
export type UserConnection = z.infer<typeof zodUserConnection>;

export type ChargerStatusMessageNode = z.infer<typeof zodChargerStatusMessageNode>;
export type ChargerStatusMessageConnection = z.infer<typeof zodzodChargerStatusMessageConnection>;
export type ChargerMonitors = z.infer<typeof zodChargerMonitors>;
export type ChargerNode = z.infer<typeof zodChargerNode>;
export type ChargersConnection = z.infer<typeof zodChargersConnection>;

export type ServiceLogsNode = z.infer<typeof zodServiceLogsNode>;
export type ServiceLogsConnection = z.infer<typeof zodServiceLogsConnection>;
export type ServiceMonitors = z.infer<typeof zodServiceMonitors>;
export type ParsedServiceErrorCountsData = z.infer<typeof zodParsedServiceErrorCountsData>;

export type VidsCount = z.infer<typeof zodVidsCount>;
export type SitesCount = z.infer<typeof zodSitesCount>;

export type ClearCacheFormArguments = z.infer<typeof zodClearCacheFormArguments>;
export type GetConfigFormArguments = z.infer<typeof zodGetConfigFormArguments>;
export type ChangeConfigFormArguments = z.infer<typeof zodChangeConfigFormArguments>;
export type GetDiagnosticsFormArguments = z.infer<typeof zodGetDiagnosticsFormArguments>;
export type ResetFormArguments = z.infer<typeof zodResetFormArguments>;
export type UpdateFirmwareFormArguments = z.infer<typeof zodUpdateFirmwareFormArguments>;
export type ConfigBuilderFormArguments = z.infer<typeof zodConfigBuilderFormArguments>;

export type ChangeConfigData = z.infer<typeof zodChangeConfigData>;
